/**
 * PageFormLayout - Reusable layout template for all create/edit form pages.
 * Provides consistent structure: breadcrumbs, header, sectioned form, sticky action bar.
 * Used by ~12 form pages across the application.
 */

import type { ReactNode } from 'react';
import {
  Box,
  Typography,
  Button,
  Breadcrumbs,
  Link,
  IconButton,
  Alert,
  Skeleton,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface FormSection {
  id: string;
  title: string;
  description?: string;
  content: ReactNode;
}

interface FormActions {
  onSave: () => void;
  onCancel: () => void;
  saveLabel?: string;
  cancelLabel?: string;
  saving?: boolean;
  saveDisabled?: boolean;
}

interface PageFormLayoutProps {
  /** Page title */
  title: string;
  /** Optional subtitle */
  subtitle?: string;
  /** Breadcrumb navigation */
  breadcrumbs?: BreadcrumbItem[];
  /** Back navigation config */
  backTo?: { label: string; href: string };
  /** Form sections */
  sections?: FormSection[];
  /** Form action buttons */
  actions: FormActions;
  /** Side panel content (for two-column layouts) */
  sidePanel?: ReactNode;
  /** Loading state */
  loading?: boolean;
  /** Error message */
  error?: string;
  /** Error dismiss handler */
  onErrorDismiss?: () => void;
  /** Content (alternative to sections for simple forms) */
  children?: ReactNode;
  /** Extra header actions */
  headerActions?: ReactNode;
}

/**
 * Reusable form page layout with modern glassmorphism design.
 */
export const PageFormLayout = ({
  title,
  subtitle,
  breadcrumbs,
  backTo,
  sections,
  actions,
  sidePanel,
  loading = false,
  error,
  onErrorDismiss,
  children,
  headerActions,
}: PageFormLayoutProps) => {
  const navigate = useNavigate();

  if (loading) {
    return (
      <Box sx={{ p: 6, animation: 'fade-in 300ms ease-out' }}>
        <Skeleton variant="text" width={200} height={24} sx={{ mb: 2, bgcolor: 'var(--glass-bg-subtle)' }} />
        <Skeleton variant="text" width={300} height={36} sx={{ mb: 6, bgcolor: 'var(--glass-bg-subtle)' }} />
        <Box
          sx={{
            p: 6,
            borderRadius: 'var(--mui-shape-customBorderRadius-lg)',
            border: '1px solid var(--glass-border)',
            background: 'var(--glass-bg-subtle)',
          }}
        >
          {[...Array(4)].map((_, i) => (
            <Box key={i} sx={{ mb: 4 }}>
              <Skeleton variant="text" width={120} height={20} sx={{ mb: 1, bgcolor: 'var(--glass-bg-subtle)' }} />
              <Skeleton variant="rounded" height={48} sx={{ borderRadius: 'var(--mui-shape-customBorderRadius-sm)', bgcolor: 'var(--glass-bg-subtle)' }} />
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 6, pb: 20, animation: 'fade-in 300ms ease-out' }}>
      {/* Back Button + Breadcrumbs */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        {backTo && (
          <IconButton
            onClick={() => navigate(backTo.href)}
            size="small"
            sx={{
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--mui-shape-customBorderRadius-sm)',
            }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        )}
        {breadcrumbs && breadcrumbs.length > 0 && (
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
            {breadcrumbs.map((crumb, index) => {
              const isLast = index === breadcrumbs.length - 1;
              if (isLast || !crumb.href) {
                return (
                  <Typography key={crumb.label} variant="body2" color="text.primary" fontWeight={500}>
                    {crumb.label}
                  </Typography>
                );
              }
              return (
                <Link
                  key={crumb.label}
                  component="button"
                  variant="body2"
                  onClick={() => crumb.href && navigate(crumb.href)}
                  sx={{
                    color: 'var(--mui-palette-text-secondary)',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    '&:hover': { color: 'var(--mui-palette-primary-main)' },
                  }}
                >
                  {crumb.label}
                </Link>
              );
            })}
          </Breadcrumbs>
        )}
      </Box>

      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 6 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: subtitle ? 1 : 0 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        {headerActions}
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" onClose={onErrorDismiss} sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {/* Form Content */}
      <Box sx={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
        {/* Main Form Area */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {sections ? (
            sections.map((section) => (
              <Box
                key={section.id}
                sx={{
                  mb: 4,
                  p: 6,
                  borderRadius: 'var(--mui-shape-customBorderRadius-lg)',
                  border: '1px solid var(--glass-border)',
                  backgroundImage: 'var(--gradient-surface)',
                }}
              >
                <Typography
                  variant="overline"
                  sx={{
                    color: 'var(--mui-palette-text-secondary)',
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                    mb: 1,
                    display: 'block',
                  }}
                >
                  {section.title}
                </Typography>
                {section.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                    {section.description}
                  </Typography>
                )}
                {!section.description && <Box sx={{ mb: 3 }} />}
                {section.content}
              </Box>
            ))
          ) : (
            <Box
              sx={{
                p: 6,
                borderRadius: 'var(--mui-shape-customBorderRadius-lg)',
                border: '1px solid var(--glass-border)',
                backgroundImage: 'var(--gradient-surface)',
              }}
            >
              {children}
            </Box>
          )}
        </Box>

        {/* Side Panel */}
        {sidePanel && (
          <Box
            sx={{
              width: 340,
              flexShrink: 0,
              position: 'sticky',
              top: 88,
            }}
          >
            <Box
              sx={{
                p: 5,
                borderRadius: 'var(--mui-shape-customBorderRadius-lg)',
                border: '1px solid var(--glass-border)',
                backgroundImage: 'var(--gradient-surface)',
              }}
            >
              {sidePanel}
            </Box>
          </Box>
        )}
      </Box>

      {/* Sticky Action Bar */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          py: 3,
          px: 6,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
          borderTop: '1px solid var(--glass-border)',
          background: 'var(--glass-bg)',
          backdropFilter: 'blur(var(--glass-blur-heavy))',
          WebkitBackdropFilter: 'blur(var(--glass-blur-heavy))',
        }}
      >
        <Button
          variant="outlined"
          color="secondary"
          onClick={actions.onCancel}
          disabled={actions.saving}
        >
          {actions.cancelLabel || 'Cancel'}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={actions.onSave}
          disabled={actions.saving || actions.saveDisabled}
          startIcon={actions.saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {actions.saving ? 'Saving...' : (actions.saveLabel || 'Save')}
        </Button>
      </Box>
    </Box>
  );
};

export default PageFormLayout;
